import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdRotateLeft } from "react-icons/md";
import { Button } from "../../core/basic/Button";
import { RT8ColumnSelector } from "../../core/blocks/MetricSelector";
import { SearchInput } from "../../fields/base/TextField";
import { createAppColumnHelper, useClientTable } from "../../table/tableFeatures";
import { usePersistedTableState } from "../../table/usePersistedTableState";
import { commaSearchFilterFn } from "../../table/filterFns";
import { resetTable } from "../../table/utils";
import { RT8Pagination } from "../../table/Pagination";
import { Table } from "../../table/Table";
import { TableErrorWrapper } from "../../table/TableErrorWrapper";
import { HStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { snakeCaseToSentenceCase } from "../../../utils/formatText";
import type { Organization } from "../../../zodios/common/orgSchema";

const columnHelper = createAppColumnHelper<Organization>();

const globalFilterFn = commaSearchFilterFn<Organization>((org) =>
  [org.id, org.name, org.profile_name, org.marketplaces, org.profile_type, org.account_manager].join(" "),
);

/** The Amazon profiles already connected to the current Trellis account. */
export function TrellisProfilesTable({
  data,
  isLoading,
  error,
}: {
  data: Organization[] | undefined;
  isLoading: boolean;
  error: unknown;
}) {
  const { t } = useTranslation(["onboarding", "common"]);

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "trellisProfilesTable",
    initialState: { sorting: [{ id: "profile_name", desc: false }] },
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor("profile_name", {
        header: t(($) => $.onboarding["Profile Name"]),
        size: 250,
        enableHiding: false,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor("name", {
        header: t(($) => $.onboarding["Organization Name"]),
        size: 250,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor((org) => org.marketplaces.join(", "), {
        id: "marketplaces",
        header: t(($) => $.onboarding["Profile Marketplaces"]),
        size: 180,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor("profile_type", {
        header: t(($) => $.onboarding["Profile Type"]),
        size: 130,
        enableResizing: false,
        sortFn: "alphanumeric",
        cell: ({ getValue }) => snakeCaseToSentenceCase(getValue()),
      }),
      columnHelper.accessor("status", {
        header: t(($) => $.onboarding.Status),
        size: 140,
        enableResizing: false,
        sortFn: "alphanumeric",
        cell: ({ getValue }) => snakeCaseToSentenceCase(getValue()),
      }),
    ],
    [t],
  );

  const table = useClientTable({
    data,
    columns,
    globalFilterFn,
    initialState,
    state,
    ...setters,
    meta: {
      showSkeleton: isLoading,
    },
  });

  return (
    <TableErrorWrapper error={error} userMessage={t(($) => $.onboarding["Error loading profiles"])}>
      <HStack padding={"small"} fontSize={"large"}>
        <Button
          variant={"contentOnly"}
          aria-label={t(($) => $.common.table["Reset table filters"])}
          onPress={() => resetTable(table)}
          fontSize={"xxlarge"}
        >
          <MdRotateLeft />
        </Button>
        <RT8ColumnSelector table={table} />
        <SearchInput
          aria-label={t(($) => $.common.actions["Search"])}
          isExpandable
          css={{ flex: "0 1 350px" }}
          value={table.state.globalFilter ?? ""}
          onChange={table.setGlobalFilter}
        />
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} />
    </TableErrorWrapper>
  );
}
